/**
 * The security evaluation scorecard, and the gate it opens.
 *
 * Six dimensions, weighted, scored 0–5 by Security — never self-graded by the requesting team.
 * That last clause is a process rule the software cannot enforce, so the UI states it and the
 * intake records who scored it; what the software CAN enforce is that the arithmetic is done in
 * one place and that the routing follows from it without anybody choosing.
 *
 * The weights sum to 100. That is asserted at module load rather than trusted, because the failure
 * mode of a weight table that sums to 95 is not an error — it is every score being quietly 5% low,
 * which moves items across the moderate/low boundary and nobody notices for a quarter.
 */

import type { RiskTier, Scorecard } from "./domain.js";
import { controlById, type ControlId, type ControlVerdict } from "./threats.js";

export interface Dimension {
  key: keyof Scorecard;
  label: string;
  weightPct: number;
  fullMarks: string;
}

export const DIMENSIONS: readonly Dimension[] = [
  {
    key: "identityAndAccess",
    label: "Identity & access",
    weightPct: 20,
    fullMarks: "Federated, scoped, short-lived credentials; named owner; every agent acts as itself",
  },
  {
    key: "dataProtection",
    label: "Data protection",
    weightPct: 20,
    fullMarks: "Classified, encrypted at rest and in transit, KMS ownership clear",
  },
  {
    key: "networkExposure",
    label: "Network exposure",
    weightPct: 15,
    fullMarks: "Behind zero trust; no public sensitive endpoints; segmented",
  },
  {
    key: "appsecAndSupplyChain",
    label: "AppSec & supply chain",
    weightPct: 15,
    fullMarks: "Pentested and scanned; dependencies pinned and reviewed",
  },
  {
    key: "monitoringAndResponse",
    label: "Monitoring & response",
    weightPct: 15,
    fullMarks: "Logs to central store; anomaly detection; tested kill switch",
  },
  {
    key: "complianceAndGovernance",
    label: "Compliance & governance",
    weightPct: 15,
    fullMarks: "Regulatory scope known; in next UAR; offboarding trigger defined",
  },
] as const;

const WEIGHT_TOTAL = DIMENSIONS.reduce((n, d) => n + d.weightPct, 0);
if (WEIGHT_TOTAL !== 100) {
  throw new Error(
    `Scorecard weights sum to ${WEIGHT_TOTAL}, not 100. Every score would be scaled by ` +
    `${(WEIGHT_TOTAL / 100).toFixed(2)}, which moves items across the risk-tier boundaries ` +
    `without changing a single answer.`);
}

export class ScorecardError extends Error {}

/**
 * `weighted = Σ(score × weight) / 100`, on the same 0–5 scale as the inputs.
 *
 * Rounded to one decimal because that is what the boundaries are expressed in — 4.0 and 2.5 — and
 * comparing an unrounded 3.9499999 against 3.95 is a boundary that moves depending on floating
 * point. Round once, here, and let every caller compare the same number the screen shows.
 */
export function weightedScore(scorecard: Scorecard): number {
  let total = 0;
  for (const d of DIMENSIONS) {
    const score = scorecard[d.key];
    if (!Number.isFinite(score) || score < 0 || score > 5) {
      throw new ScorecardError(
        `${d.label} scored ${String(score)}; the scale is 0–5. A dimension outside it would ` +
        `change the weighted total without being visibly wrong on the screen.`);
    }
    total += score * d.weightPct;
  }
  return Math.round((total / 100) * 10) / 10;
}

/**
 * ≥ 4.0 low · 2.5–3.9 moderate · < 2.5 high.
 *
 * Note which way round this runs: a HIGH score is LOW risk. The scorecard measures how well
 * controlled the thing is, so five out of five means little scrutiny is needed. Reading it the
 * other way inverts every gate, which is why the function returns a named tier rather than a
 * number for a caller to compare.
 */
export function riskTierFor(weighted: number): RiskTier {
  if (weighted >= 4.0) return "low";
  if (weighted >= 2.5) return "moderate";
  return "high";
}

export interface GateOutcome {
  riskTier: RiskTier;
  weighted: number;
  /** Who decides, in words the wizard prints under the score. */
  routesTo: string;
  /** Does this need an approver from outside the requesting team, holding a veto? */
  requiresIndependentApproval: boolean;
  /** Must a remediation plan accompany the submission? */
  requiresRemediationPlan: boolean;
}

export function gateFor(scorecard: Scorecard): GateOutcome {
  const weighted = weightedScore(scorecard);
  const riskTier = riskTierFor(weighted);
  switch (riskTier) {
    case "low":
      return {
        riskTier, weighted,
        routesTo: "the owning team",
        requiresIndependentApproval: false,
        requiresRemediationPlan: false,
      };
    case "moderate":
      return {
        riskTier, weighted,
        routesTo: "security review, with a remediation plan",
        requiresIndependentApproval: true,
        requiresRemediationPlan: true,
      };
    case "high":
      return {
        riskTier, weighted,
        routesTo: "the full gate, with an independent veto",
        requiresIndependentApproval: true,
        requiresRemediationPlan: true,
      };
  }
}

import type { ChecklistItem } from "./domain.js";

export interface ChecklistVerdict {
  complete: boolean;
  /** Mandatory items not yet done or waived. Submission is blocked while this is non-empty. */
  blocking: ChecklistItem[];
  /** Items waived without a reason. Also blocking — a waiver with no reason is not a waiver. */
  unjustifiedWaivers: ChecklistItem[];
}

/**
 * Submission is blocked while any mandatory item is outstanding.
 *
 * The waiver check is separate from the completeness check on purpose. "Waived" is a legitimate
 * terminal state and the escape hatch the process needs; a waiver with an empty reason is the
 * escape hatch being used to make the button go green, and it produces a gate that has been passed
 * with no record of what was skipped or why.
 */
export function validateChecklist(items: readonly ChecklistItem[]): ChecklistVerdict {
  const blocking = items.filter(
    (i) => i.mandatory && i.status !== "complete" && i.status !== "waived");
  const unjustifiedWaivers = items.filter(
    (i) => i.status === "waived" && (i.waiverReason === null || i.waiverReason.trim() === ""));
  return {
    complete: blocking.length === 0 && unjustifiedWaivers.length === 0,
    blocking,
    unjustifiedWaivers,
  };
}

// ── gaps the estate opens against a dimension ──────────────────────────────────────────────────

/**
 * A control whose state, for THIS estate, means a dimension cannot honestly carry full marks.
 *
 * The scorecard is scored by Security, not computed, and that stays true. What the software can
 * add is the one thing a scorer cannot see from the intake form: what the live estate says. A gap
 * is printed beside the dimension it belongs to, naming the control and the reason, so that a 5
 * on "Identity & access" for a tenant whose agents act as people is a choice somebody made with
 * the fact in front of them, not a default.
 */
export interface ScorecardGap {
  dimension: keyof Scorecard;
  control: ControlId;
  /** Why the gap is open, in the reviewer's words. */
  reason: string;
}

/**
 * The gaps the estate opens against the scorecard.
 *
 * One rule today: a tenant that has agents and cannot show `agent-own-identity` in force has a
 * gap on identity and access. That control is manual (see its `whyManual`), so the gap is open
 * for every agent-bearing tenant until a detection feed exists — and that is the correct answer,
 * because the alternative is a scorecard that gives full marks for identity to an estate whose
 * agents may all be acting under somebody's cookie.
 *
 * A tenant with no agents has no gap: there is nobody to blend. And the gap closes only on a
 * MEASURED in-force verdict for an automatic control, exactly as `coverageOf` refuses to promote a
 * manual control on an optimistic verdict. Any looser and the first integration that posts
 * `in-force` for a control it cannot see would clear the gap without anybody deciding to.
 */
export function scorecardGaps(
  estate: { agentCount: number },
  verdicts: ReadonlyMap<ControlId, ControlVerdict>,
): ScorecardGap[] {
  const gaps: ScorecardGap[] = [];

  if (estate.agentCount > 0) {
    const control = controlById("agent-own-identity");
    const verdict = verdicts.get(control.id);
    const measuredInForce = control.assurance === "automatic" && verdict?.state === "in-force";
    if (!measuredInForce) {
      const why = verdict?.state === "failing"
        ? `is failing: ${verdict.detail || "no detail was recorded"}`
        : "is unmeasured. Unmeasured is not mitigated: nothing here can show these agents act as " +
          "themselves rather than through a person's session";
      gaps.push({
        dimension: "identityAndAccess",
        control: control.id,
        reason:
          `${estate.agentCount} agent${estate.agentCount === 1 ? " is" : "s are"} registered and ` +
          `"${control.name}" ${why}, so identity and access cannot carry full marks.`,
      });
    }
  }

  return gaps;
}
